import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { DATE_MODES } from '../../utils/dateUtils';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';

const MODES = [
  { mode: DATE_MODES.MONTH, key: 'month' },
  { mode: DATE_MODES.WEEK, key: 'week' },
  { mode: DATE_MODES.YEAR, key: 'year' },
  { mode: DATE_MODES.ALL, key: 'all' },
];

/** A 52px choice row with a check on the current one. */
function ModeRow({ label, selected, onClick }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      w="100%"
      minH="52px"
      px="20px"
      textAlign="left"
      color="redesign.heading"
      _active={{ bg: 'redesign.bg.hover' }}
      aria-pressed={selected}
    >
      <Text textStyle="phoneBody">{label}</Text>
      {selected && <Box as={LuCheck} boxSize="20px" color="phone.link" />}
    </Box>
  );
}

function PeriodChoices({ dateRange, onMode, onCustom }) {
  const { t } = useTranslation('movements');
  const { close } = usePhoneSheet();
  const [start, setStart] = useState(dateRange?.start_date || '');
  const [end, setEnd] = useState(dateRange?.end_date || '');
  const isCustom = dateRange?.mode === DATE_MODES.CUSTOM;
  const valid = Boolean(start && end && start <= end);

  // A filter is applied only once the sheet's history entry is gone, so the
  // pending back step cannot undo it.
  const pick = async (mode) => {
    await close();
    onMode(mode);
  };
  const applyCustom = async () => {
    if (!valid) return;
    await close();
    onCustom(start, end);
  };

  return (
    <Box pb="8px">
      {MODES.map(({ mode, key }) => (
        <ModeRow
          key={mode}
          label={t(`filters:dateRange.modes.${key}`)}
          selected={dateRange?.mode === mode}
          onClick={() => pick(mode)}
        />
      ))}

      <Box px="20px" pt="16px" borderTop="1px solid" borderColor="redesign.border" mt="4px">
        <Flex align="center" justify="space-between" mb="10px">
          <Text textStyle="phoneHeadline" color="redesign.heading">{t('phone.period.customRange')}</Text>
          {isCustom && <Box as={LuCheck} boxSize="20px" color="phone.link" />}
        </Flex>
        {/* Side by side the native inputs clip the year below 360px. */}
        <Flex gap="12px" sx={{ '@media (max-width: 359.98px)': { flexDirection: 'column' } }}>
          <Box flex="1" minW={0}>
            <Text as="label" htmlFor="phone-period-from" textStyle="phoneMeta" color="phone.muted">
              {t('phone.period.from')}
            </Text>
            <Input
              id="phone-period-from"
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setStart(e.target.value)}
              h="44px"
              fontSize="16px"
              mt="4px"
            />
          </Box>
          <Box flex="1" minW={0}>
            <Text as="label" htmlFor="phone-period-to" textStyle="phoneMeta" color="phone.muted">
              {t('phone.period.to')}
            </Text>
            <Input
              id="phone-period-to"
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              h="44px"
              fontSize="16px"
              mt="4px"
            />
          </Box>
        </Flex>
        <Button variant="phonePrimary" w="100%" mt="14px" isDisabled={!valid} onClick={applyCustom}>
          {t('phone.period.apply')}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * The Movements period sheet: Month · Week · Year · All, then a custom range
 * from two native date inputs. The desktop's typed-period box stays there.
 */
export default function PhonePeriodSheet({ isOpen, onClose, dateRange, onMode, onCustom }) {
  const { t } = useTranslation('movements');
  const [key, setKey] = useState(0);
  // Each opening starts from the current range, not from what was typed and
  // abandoned last time.
  useEffect(() => { if (isOpen) setKey((k) => k + 1); }, [isOpen]);

  return (
    <PhoneSheet id="movements-period" isOpen={isOpen} onClose={onClose} title={t('phone.period.title')}>
      <PeriodChoices key={key} dateRange={dateRange} onMode={onMode} onCustom={onCustom} />
    </PhoneSheet>
  );
}
